/** COMMON IMPORTS */
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { BrowserRouter as Router, Switch, Route, useHistory } from "react-router-dom";
import PrivateRoute from "./components/reuseableComponents/auth/PrivateRoute";
import { UserContext } from "./components/reuseableComponents/auth/UserContext";
import { EnvironmentProvider } from "./configuration/EnvironmentContext";
import URL404 from "./components/reuseableComponents/URL404";
import Footer from "./components/reuseableComponents/navigation/Footer";

/** ROUTE IMPORTS */
import { archiveRoutes } from "./routes/archiveRoutes";
import { ddwAdminRoutes } from "./routes/ddwAdminRoutes.js";
import { enforcementRoutes } from "./routes/enforcementRoutes";
import { publicRoutes } from "./routes/publicRoutes";
import { portalRoutes } from "./routes/portalRoutes";

/** UTAH DESIGN SYSTEM IMPORTS */
import { appConfig } from "./configuration/AppConfig";
import { setUtahHeaderSettings } from "@utahdts/utah-design-system-header";
import logo from "./images/DDW_LOGOS/Logo-DDW.svg"
import "@utahdts/utah-design-system/css";
import "@utahdts/utah-design-system-header/css";
import "./css/utahDesign.css";

/** Dynamic Data for Menu Items */
import { useApolloClient } from "@apollo/client";
import { GET_MENU_ITEMS } from "./queries/menuQrys";
import { GET_LOGGEDIN_USER_QUERY } from "./queries/ddwAdminQrys/UserManagementQrys";

/** Sidebar Imports */
import SidePanel from "./components/reuseableComponents/SidePanel.js";
import WaterSystemSearchModal from "./components/reuseableComponents/WaterSystemSearchModal";
import { WaterSystemSearchModalProvider } from "./components/reuseableComponents/WaterSystemSearchModalContext.js";

/** Status Bar Imports */
import StatusBar from "./components/reuseableComponents/StatusBar";
import { HoverProvider } from "./context/HoverContext.js";
import { setUserProfile } from "./components/reuseableComponents/auth/UserProfiles.js";

/**
 * Utility to retrieve a cookie value by name
 */
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const ACCESS_TOKEN_KEY = "token"; 

/**TODO:
 * remove Header.js once we finish refacturing code.
 */
function HeaderComponent({ setPanelMenu, providerUser, logout, menuItemsArray, setAuthCookie, redirectPath, setRedirectPath }) {
  const history = useHistory();

  useEffect(() => {
    if (redirectPath) {
      history.push(redirectPath);
      setRedirectPath(null);
    }
  }, [redirectPath, history, setRedirectPath]);
  const showSidePanel = useCallback((menuItemCode) => {
    setTimeout(() => {
      const sidePanelContainer = document.querySelector(".side-panel-container");
      if (sidePanelContainer) {
        sidePanelContainer.classList.remove("hidden");
        sidePanelContainer.classList.add("visible");
      } else {
        console.warn("Side panel container not found in DOM.");
      }
    }, 0);
    setPanelMenu(menuItemCode);
  }, [setPanelMenu]);

  // Add this effect to set sidePanelMenu based on URL when HeaderComponent renders
  useEffect(() => {
    const pathname = window.location.pathname;
    if (!menuItemsArray || menuItemsArray.length === 0) return;
    
    const sidePanelVisible = document.querySelector('.side-panel-container')?.classList.contains('visible');
    if (pathname === "/" || pathname === "/public-index" || sidePanelVisible) return;

    const pathMatch = [
      { key: "home", code: "HOME" },
      { key: "ddw-admin", code: "DDW_ADMIN" },
      { key: "portal", code: "PORTAL" },
      { key: "public", code: "PUBLIC" }
    ].find(m => pathname.includes(m.key));
  
    if (pathMatch) {
      setPanelMenu(pathMatch.code);
      showSidePanel(pathMatch.code);
    }
  }, [menuItemsArray, setPanelMenu, showSidePanel]);

  useEffect(() => {
    setUtahHeaderSettings({
      title: "Waterlink",
      utahId: {
        currentUser: providerUser?.user?.firstName ? {
          first: providerUser.user.firstName,
          last: providerUser.user.lastName,
          email: providerUser.user.email,
          authenticated: sessionStorage?.auth ? JSON.parse(sessionStorage.auth) : false
        } : null,
        onSignIn: () => {
          setAuthCookie("v2Authenticated", "true");
          window.location.assign(`${appConfig.authEndpoint}${appConfig.authParams}`);
        },
        onSignOut: () => {
          logout();
        },
      },
      logo: {
        htmlString: '<img src="' + logo + '" id="ddw-logo" alt="Logo-DDW" />'
      },
      mainMenu: {
        menuItems: Array.isArray(menuItemsArray) ? menuItemsArray : [],
      },
      footer: {
        showHorizontalRule: true,
      },
    });
  }, [providerUser, logout, menuItemsArray, setAuthCookie]);

  return null;
}

const App = () => {
  const client = useApolloClient();
  const [isSyncing, setIsSyncing] = useState(false);
  const [user, setUser] = useState(
    sessionStorage.getItem("user") ? JSON.parse(sessionStorage.user) : (
      sessionStorage.defaultUser ? JSON.parse(sessionStorage.defaultUser) : null
    )
  );
  const [menuItemsArray, setMenuItemsArray] = useState([]);
  const [showSystemSelectModal, setShowSystemSelectModal] = useState(false);
  const [isSidePanelVisible, setIsSidePanelVisible] = useState(false);
  const [panelMenu, setPanelMenu] = useState("");
  const [selectedWaterSystem, setSelectedWaterSystem] = useState(
    sessionStorage.getItem("selectedWaterSystem")
      ? JSON.parse(sessionStorage.getItem("selectedWaterSystem"))
      : null
  );
  const [redirectPath, setRedirectPath] = useState(null);
  const hasInitialized = useRef(false);

  const destroyAuthCookie = (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  };

  const getChildMenuItems = useCallback((children) => {
    return children.map((child) => ({
      title: child.label.toUpperCase(),
      isSelected: false,
      ...(child.children?.length > 0 
        ? { actionMenu: getChildMenuItems(child.children), childrenMenuType: 'DROPDOWN' }
        : { actionUrl: { url: child.url_route } }
      )
    }));
  }, []);
  
  const processMenu = useCallback((menuData, showSidePanel, currentPanelMenu) => {
    if (!menuData || !menuData.getMenuItems) return [];

    return menuData.getMenuItems.sections.map((menuItem) => {
      if (menuItem.children && menuItem.children.length > 0) {
        return {
          id: menuItem.code,
          title: menuItem.label.toUpperCase(),
          isSelected: (currentPanelMenu === menuItem.code),
          actionFunction: () => {
            showSidePanel(menuItem.code);
          },
          children: menuItem.children, 
          formattedChildren: getChildMenuItems(menuItem.children) 
        };
      } else {
        return {
          title: menuItem.label.toUpperCase(),
          isSelected: (currentPanelMenu === menuItem.code),
          actionUrl: {
            url: menuItem.url_route,
          },
        };
      }
    });
  }, [getChildMenuItems]);

  const showSidePanel = useCallback((menuItemCode) => {
    setIsSidePanelVisible(true);
    setPanelMenu(menuItemCode);
  }, []);

  /** * Orchestrates the sequential loading of User and Menu 
   */
  const initializeApp = useCallback(async () => {
    // if (isSyncing) return;
    console.log("Initializing application...");
    
    try {
      setIsSyncing(true);
      const v2AuthCookie = getCookie("v2Authenticated");

      const user = sessionStorage.getItem("user");
      const savedSystem = sessionStorage.getItem("selectedWaterSystem");
      if (savedSystem) {
        setSelectedWaterSystem(JSON.parse(savedSystem));
      }

      // Fetch user data if we don't already have it, but have successfully logged in to either environment
      if (!user && (v2AuthCookie === "true")) {
        const { data: userData } = await client.query({
          query: GET_LOGGEDIN_USER_QUERY,
          fetchPolicy: 'network-only',
        });

        if (userData?.getLoggedInUser) {
          const validatedUser = setUserProfile(userData.getLoggedInUser);
          setUser(validatedUser);
          sessionStorage.setItem("user", JSON.stringify(validatedUser));
          sessionStorage.setItem("auth", "true");
          
          if (validatedUser.permissions.length === 0) {
            setRedirectPath("/public/request-access");
          }
        }
      }

      const { data: menuData } = await client.query({
        query: GET_MENU_ITEMS,
        fetchPolicy: 'network-only',
      });

      setMenuItemsArray(() => processMenu(menuData, showSidePanel, panelMenu));

    } catch (err) {
      console.error("App initialization failed:", err);
    } finally {
      setIsSyncing(false); // Unlocks the UI
    }
  }, [client, processMenu, showSidePanel, panelMenu]);

  useEffect(() => {
    if (!isSyncing && !hasInitialized.current) {
      initializeApp();
      hasInitialized.current = true;
    }
  }, [isSyncing, initializeApp]);
  
  const providerUser = useMemo(() => ({ 
    user, 
    setUser, 
    isSyncing // Pass this down so PrivateRoute can see it
  }), [user, setUser, isSyncing]);

  // Add this inside the App component
const processedMenuItems = useMemo(() => {
  return menuItemsArray.map(item => ({
    ...item,
    isSelected: item.id === panelMenu
  }));
}, [menuItemsArray, panelMenu]);

  const setAuthCookie = useCallback((name, value) => {
    const expires = new Date(Date.now() + 10 * 60 * 60 * 1000).toUTCString(); // 10 hours
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
  }, []);

  const logout = () => {
    destroyAuthCookie("v2Authenticated");    
    const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("auth");
    sessionStorage.removeItem("token");

    const revokeUrl = token && token !== "null" 
        ? `${appConfig.revoke}${appConfig.authParams}&jwt=${token}`
        : `${appConfig.revoke}${appConfig.authParams}`;

    window.location.assign(revokeUrl);
  };

  return (
    <EnvironmentProvider>
      <UserContext.Provider value={providerUser}>
        <WaterSystemSearchModalProvider>
          <HoverProvider>
            <Router>
              <HeaderComponent
                setPanelMenu={setPanelMenu}
                setMenuItemsArray={setMenuItemsArray}
                providerUser={providerUser}
                logout={logout}
                user={user}
                menuItemsArray={processedMenuItems}
                panelMenu={panelMenu}
                setAuthCookie={setAuthCookie}
                redirectPath={redirectPath}
                setRedirectPath={setRedirectPath}
              />
              <WaterSystemSearchModal
                showSystemSelectModal={showSystemSelectModal}
                setShowSystemSelectModal={setShowSystemSelectModal}
                selectedWaterSystem={selectedWaterSystem}
                setSelectedWaterSystem={setSelectedWaterSystem}
              />
              {isSyncing ? (
                <div className="utah-id-syncing"><em>Checking Login Status...</em></div>
              ) : (
                <div className="waterlink-container">
                  <div className={`side-panel-container ${isSidePanelVisible ? 'visible' : 'hidden'}`}>
                    {panelMenu !== "" && Array.isArray(processedMenuItems) && (() => {
                      const activeMenuItem = processedMenuItems.find(item => item.id === panelMenu);
                      return activeMenuItem && activeMenuItem.children ? (
                        <SidePanel
                          menuItems={activeMenuItem.children}
                          selectedWaterSystem={selectedWaterSystem}
                        />
                      ) : null;
                    })()}
                  </div>
                  <div className="main-content-container d-flex flex-column">
                    <Switch>
                      {/* PUBLIC ROUTES */}
                      {publicRoutes.map((entry) => (
                        <Route
                          key={entry.path}
                          exact
                          path={entry.path}
                        >
                          <entry.component
                            showSystemSelectModal={showSystemSelectModal}
                            setShowSystemSelectModal={setShowSystemSelectModal}
                            selectedWaterSystem={selectedWaterSystem}
                            setSelectedWaterSystem={setSelectedWaterSystem}
                          />
                        </Route>
                      ))}
                      {/*SECURE ROUTES*/}
                      {archiveRoutes.map((entry) => (
                        <PrivateRoute
                          key={entry.path}
                          exact
                          path={entry.path}
                          component={entry.component}
                          role={!user || !user.isPublic}
                        />
                      ))}
                      {ddwAdminRoutes.map((entry) => (
                        <PrivateRoute
                          key={entry.path}
                          exact
                          path={entry.path}
                          component={entry.component}
                          role={user && user.permissions?.includes("user_management")}
                        />
                      ))}
                      {enforcementRoutes.map((entry) => (
                        <PrivateRoute
                          key={entry.path}
                          exact
                          path={entry.path}
                          component={entry.component}
                          role={!user.isPublic}
                        />
                      ))}
                      {portalRoutes.map((entry) => (
                        <PrivateRoute
                          key={entry.path}
                          exact
                          path={entry.path}
                          component={entry.component}
                          role={user && user.permissions?.includes("portal_access")}
                        />
                      ))}
                      <Route component={URL404} />
                    </Switch>
                  </div>
                </div>
              )}
              <StatusBar />
              <Footer />
            </Router>
          </HoverProvider>
        </WaterSystemSearchModalProvider>
      </UserContext.Provider>
    </EnvironmentProvider>
  );
}

export default App;
